import { useEffect, useRef, useState } from 'react'
import { Modal, Row, Tab, Tabs } from 'react-bootstrap'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { buildAlert } from '@/actions/index.js'
import { getPersonNumber } from '@/actions/persons/personnumberget.js'
import { postPerson } from '@/actions/persons/personpost.js'
import { AttachmentView } from '@/common/attachments/attachmentview.jsx'
import { Button } from '@/common/Button.tsx'
import { IconAddNew } from '@/common/icons.jsx'
import { fieldParams } from '@/common/person/personmodalfieldparams.js'
import { findPersonModalFormErrors } from '@/common/person/personmodalvalidation.js'
import { formBIC, getCountryOptions, localDate } from '@/common/utils.jsx'
import DiscussionView from '@/mgmtview/discussions/index.jsx'
import { actionTypeSelector, authSelector } from '@/reducers/index.js'
import EmploymentEndBanner from '../EmploymentEndBanner.jsx'
import ContractInfo from '../tabs/ContractInfo.jsx'
import { OrganizationalData } from '../tabs/organizationaldata.jsx'
import { PersonalDetails } from '../tabs/personaldetails.jsx'
import { PersonSettings } from '../tabs/personsettings.jsx'
import ExternalWorkforceCompetencesTab from './ExternalWorkforceCompetencesTab.jsx'
import ExternalWorkforceFooter from './ExternalWorkforceFooter.jsx'
import ExternalWorkforceHeader from './ExternalWorkforceHeader.jsx'
import emptyPerson from './Utils/EmptyExternalWorkforce.js'

const TYPING_DELAY = 200

const ExternalWorkforceModal = ({ show, setShow, personData }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const {
    eSalaryCompanyId,
    hrLevel,
    knowledge,
    discussions,
    personAttachments,
  } = useSelector(authSelector)
  const actionType = useSelector(actionTypeSelector)
  const isBasicLevel = hrLevel !== 'premium'

  const formMethods = useForm()
  const fieldTimers = useRef({})

  const [activeTab, setActiveTab] = useState('person')
  const [person, setPerson] = useState(
    personData?.personId ? personData : emptyPerson,
  )
  const [formErrors, setFormErrors] = useState({})
  const [submitForm, setSubmitForm] = useState(false)
  const [countryOptions, setCountryOptions] = useState([])
  const [loadCompetences, setLoadCompetences] = useState(false)
  const [loadDiscussions, setLoadDiscussions] = useState(false)

  useEffect(() => {
    setPerson(personData?.personId ? personData : emptyPerson)
  }, [personData])

  useEffect(() => {
    if (show) {
      formMethods.reset()
      getCountryOptions(setCountryOptions)
      if (!person.personId) dispatch(getPersonNumber())
    } else {
      Object.values(fieldTimers.current).forEach(clearTimeout)
      fieldTimers.current = {}
      setActiveTab('person')
      setLoadCompetences(false)
      setLoadDiscussions(false)
    }
  }, [show])

  useEffect(() => {
    if (
      actionType === 'POST_PERSON_SUCCEEDED' ||
      actionType === 'POST_PERSON_EDIT_SUCCEEDED'
    ) {
      setSubmitForm(false)
      setFormErrors({})
      setShow(false)
    } else if (actionType.endsWith('_FAIL') || actionType === 'ALERT') {
      setSubmitForm(false)
    }
  }, [actionType, setShow])

  const debounce = (id, fn) => {
    clearTimeout(fieldTimers.current[id])
    fieldTimers.current[id] = setTimeout(fn, TYPING_DELAY)
  }

  const handleValidation = (e) => {
    const newErrors = findPersonModalFormErrors(t, e, formErrors, person)
    if (e.target.id === 'iban' && newErrors.iban === undefined) {
      const newBic = formBIC(e.target.value)
      if (newBic) {
        setPerson((prev) => ({
          ...prev,
          details: { ...prev.details, bic: newBic },
        }))
      }
    }
    setFormErrors(newErrors)
    return newErrors
  }

  const handlePerson = (e) => {
    const { id, value } = e.target
    debounce(id, () => {
      setPerson((prev) => ({ ...prev, [id]: value }))
      handleValidation(e)
    })
  }

  const handleDetails = (e) => {
    const { id, value } = e.target
    debounce(id, () => {
      setPerson((prev) => ({
        ...prev,
        details: { ...prev.details, [id]: value },
      }))
      handleValidation(e)
    })
  }

  const handleControlDetails = (e) => {
    const id = e.target.id
    const value =
      e.target.type === 'checkbox' ? e.target.checked : e.target.value
    debounce(id, () => {
      handleValidation(e)
      setPerson((prev) => ({
        ...prev,
        controlDetails: { ...prev.controlDetails, [id]: value },
      }))
    })
  }

  const handleContractDetails = (e) => {
    const id = e.target.id
    const value =
      e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setPerson((prev) => ({
      ...prev,
      externalContractInfo: { ...prev.externalContractInfo, [id]: value },
    }))
  }

  const handleContractCodeItem = (newValue, actionMeta) => {
    const { name } = actionMeta
    const codeItemId = newValue ? newValue.value : null
    setPerson((prev) => ({
      ...prev,
      externalContractInfo: {
        ...prev.externalContractInfo,
        [name]: codeItemId ? { codeItemId } : null,
      },
    }))
  }

  const handleOtherDetails = (e) => {
    const { id, value } = e.target
    debounce(id, () => {
      handleValidation(e)
      setPerson((prev) => ({
        ...prev,
        otherDetails: { ...prev.otherDetails, [id]: value },
      }))
    })
  }

  const clampPercent = (v) => {
    const n = Number(v)
    if (!Number.isFinite(n)) return 0
    return Math.max(0, Math.min(100, n))
  }

  const handleDimensionAllocChange = (codeKey, codeValue) => (allocs) => {
    const items = (allocs || [])
      .filter((a) => a && a.id != null)
      .map((a) => ({
        codeKey: a.id,
        codeValue: a.codeValue,
        percent: clampPercent(a.percent),
      }))

    setPerson((prev) => {
      const dimensions = Array.isArray(prev.dimensions)
        ? prev.dimensions.slice()
        : []
      const idx = dimensions.findIndex((d) => d.codeKey === codeKey)
      if (!items.length) {
        if (idx !== -1) dimensions.splice(idx, 1)
      } else {
        const next = { codeKey, codeValue, items }
        if (idx === -1) dimensions.push(next)
        else dimensions[idx] = next
      }
      return { ...prev, dimensions }
    })
  }

  const selectChange = (newValue, actionMeta) => {
    const { name } = actionMeta
    const value = newValue === null ? '' : newValue.value
    switch (name) {
      case 'country':
        setPerson((prev) => ({
          ...prev,
          details: { ...prev.details, country: value },
        }))
        break
      case 'countryCode':
      case 'nationality':
      case 'nationalitySecondary':
        setPerson((prev) => ({
          ...prev,
          nationalDetails: { ...prev.nationalDetails, [name]: value },
        }))
        break
      case 'language':
      case 'dimension6':
        setPerson((prev) => ({
          ...prev,
          controlDetails: { ...prev.controlDetails, [name]: value },
        }))
        break
    }
  }

  const handleClose = () => {
    dispatch({ type: 'GET_PERSON_CANCELLED' })
    setActiveTab('person')
    setShow(false)
    setFormErrors({})
    setSubmitForm(false)
  }

  const handleEmploymentEnd = () => {
    setPerson((prev) => ({
      ...prev,
      externalContractInfo: {
        ...prev.externalContractInfo,
        externalEndDate: localDate(),
      },
      employmentDetails: {
        ...prev.employmentDetails,
        personStatus: 'ENDED_EXTERNAL',
      },
    }))
    setActiveTab('contractInfo')
  }

  const handleEmploymentOpen = () => {
    setPerson((prev) => ({
      ...prev,
      externalContractInfo: {
        ...prev.externalContractInfo,
        externalEndDate: null,
      },
      employmentDetails: {
        ...prev.employmentDetails,
        personStatus: 'NOT_EMPLOYED',
      },
    }))
  }

  const handleShow = () => {
    setPerson(emptyPerson)
    setShow(true)
  }

  const handleSubmit = () => {
    setSubmitForm(true)
    setTimeout(() => {
      const newErrors = findPersonModalFormErrors(t, null, null, person)
      if (Object.keys(newErrors).length > 0) {
        setSubmitForm(false)
        setFormErrors(newErrors)
        dispatch(
          buildAlert(
            'ERROR',
            t('FormContainsErrors', 'Lomakkeella on virheitä'),
            t('PleaseFixFormErrors', 'Ole hyvä ja korjaa virheet'),
            Object.values(newErrors),
          ),
        )
        return
      }
      const payload = { ...person }
      if (payload.personId === null) delete payload.personId
      const externalEndDate = payload.externalContractInfo?.externalEndDate
      payload.employmentDetails = {
        ...payload.employmentDetails,
        personStatus: externalEndDate ? 'ENDED_EXTERNAL' : 'NOT_EMPLOYED',
      }
      dispatch(postPerson(payload))
    })
  }

  if (!show) {
    return (
      <Button
        testId="add-external-party"
        className="addbutton"
        onClick={handleShow}
        variant="primary"
      >
        <IconAddNew /> {t('AddExternalParty', 'Lisää ulkopuolinen toimija')}
      </Button>
    )
  }

  return (
    <>
      <Button
        testId="add-external-party"
        className="addbutton"
        onClick={handleShow}
        variant="primary"
      >
        <IconAddNew /> {t('AddExternalParty', 'Lisää ulkopuolinen toimija')}
      </Button>
      <Modal
        show
        onHide={handleClose}
        backdrop="static"
        size="xl"
        fullscreen="lg-down"
        scrollable
      >
        <ExternalWorkforceHeader
          person={person}
          eSalaryCompanyId={eSalaryCompanyId}
          submitForm={submitForm}
        />

        <FormProvider {...formMethods}>
          <Modal.Body className="d-flex flex-column p-0" as="form">
            <Tabs
              onSelect={setActiveTab}
              activeKey={activeTab}
              defaultActiveKey="person"
              className="scrollable mb-3"
            >
              <Tab
                eventKey="person"
                title={t('PersonalDetails', 'Henkilötiedot')}
              >
                <EmploymentEndBanner
                  endDate={person.externalContractInfo?.externalEndDate}
                />
                <PersonalDetails
                  person={person}
                  setPerson={setPerson}
                  handlePerson={handlePerson}
                  handleDetails={handleDetails}
                  handleOtherDetails={handleOtherDetails}
                  handleControlDetails={handleControlDetails}
                  selectChange={selectChange}
                  countryOptions={countryOptions}
                  fieldParams={fieldParams}
                  formErrors={formErrors}
                  isPersonal={false}
                />
              </Tab>

              <Tab
                eventKey="organizationalData"
                title={t('OrganizationalData', 'Organisaatiotiedot')}
              >
                <EmploymentEndBanner
                  endDate={person.externalContractInfo?.externalEndDate}
                />
                <OrganizationalData
                  person={person}
                  selectChange={selectChange}
                  handleControlDetails={handleControlDetails}
                  fieldParams={fieldParams}
                  formErrors={formErrors}
                  isPersonal={false}
                  handleDimensionAllocChange={handleDimensionAllocChange}
                />
              </Tab>

              <Tab
                eventKey="contractInfo"
                title={t('ContractInfo', 'Sopimustiedot')}
              >
                <EmploymentEndBanner
                  endDate={person.externalContractInfo?.externalEndDate}
                />
                <ContractInfo
                  key={person.personId ?? 'new'}
                  contractDetails={person.externalContractInfo}
                  handleContractDetails={handleContractDetails}
                  handleContractCodeItem={handleContractCodeItem}
                  formErrors={formErrors}
                  isPersonal={false}
                />
              </Tab>

              {person.personId && knowledge && !isBasicLevel && (
                <Tab
                  eventKey="competenceDetails"
                  title={t('Competence', 'Osaaminen')}
                  onEnter={() => setLoadCompetences(true)}
                >
                  <EmploymentEndBanner
                    endDate={person.externalContractInfo?.externalEndDate}
                  />
                  {loadCompetences && (
                    <ExternalWorkforceCompetencesTab
                      person={person}
                      handleDetails={handleDetails}
                      formErrors={formErrors}
                    />
                  )}
                </Tab>
              )}

              {person.personId && discussions && !isBasicLevel && (
                <Tab
                  eventKey="discussions"
                  title={t('Discussions', 'Keskustelut')}
                  onEnter={() => setLoadDiscussions(true)}
                >
                  <EmploymentEndBanner
                    endDate={person.externalContractInfo?.externalEndDate}
                  />
                  {loadDiscussions && (
                    <Row className="h-450">
                      <DiscussionView person={person} />
                    </Row>
                  )}
                </Tab>
              )}

              {personAttachments && (
                <Tab
                  eventKey="attachments"
                  title={t('Attachments', 'Liitteet')}
                >
                  <EmploymentEndBanner
                    endDate={person.externalContractInfo?.externalEndDate}
                  />
                  <AttachmentView
                    section="person"
                    entityid={person.personId}
                    attachments={person.attachments}
                    savedId={
                      actionType === 'POST_PERSON_SUCCEEDED' ||
                      actionType === 'POST_PERSON_EDIT_SUCCEEDED'
                        ? person.personId
                        : undefined
                    }
                    isPersonal={false}
                  />
                </Tab>
              )}

              <Tab eventKey="personSettings" title={t('Settings', 'Asetukset')}>
                <EmploymentEndBanner
                  endDate={person.externalContractInfo?.externalEndDate}
                />
                <PersonSettings person={person} setPerson={setPerson} />
              </Tab>
            </Tabs>
          </Modal.Body>
        </FormProvider>

        <ExternalWorkforceFooter
          handleClose={handleClose}
          handleSubmit={handleSubmit}
          submitForm={submitForm}
          person={person}
          handleEmploymentEnd={handleEmploymentEnd}
          handleEmploymentOpen={handleEmploymentOpen}
        />
      </Modal>
    </>
  )
}

export default ExternalWorkforceModal
